import { useMemo } from 'react';
import { ChoiceDrill } from './ChoiceDrill';
import { makeDataRound, type DataChart, type DataTable } from './data';
import type { DrillProps } from './meta';

function Table({ t }: { t: DataTable }) {
  return (
    <table className="data-table">
      <caption>{t.title}{t.unit && <span className="muted"> (단위: {t.unit})</span>}</caption>
      <thead><tr>{t.head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
      <tbody>
        {t.rows.map((row) => (
          <tr key={String(row[0])}>{row.map((c, i) => (i === 0 ? <th key={i} scope="row">{c}</th> : <td key={i}>{typeof c === 'number' ? c.toLocaleString('ko-KR') : c}</td>))}</tr>
        ))}
      </tbody>
    </table>
  );
}

function Chart({ c }: { c: DataChart }) {
  const w = 320, h = 170, base = 140, top = 24;
  const max = Math.max(...c.values);
  const bw = w / c.values.length;
  return (
    <figure className="data-chart">
      <figcaption>{c.title} <span className="muted">(단위: {c.unit})</span></figcaption>
      <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={`${c.title}: ${c.labels.map((l, i) => `${l} ${c.values[i]}`).join(', ')}`}>
        <line x1="0" y1={base} x2={w} y2={base} className="axis" />
        {c.values.map((v, i) => {
          const bh = ((base - top) * v) / max;
          return (
            <g key={c.labels[i]}>
              <rect x={i * bw + bw * 0.2} y={base - bh} width={bw * 0.6} height={bh} className="chart-bar" rx="3" />
              <text x={i * bw + bw / 2} y={base - bh - 6} textAnchor="middle" className="val">{v}</text>
              <text x={i * bw + bw / 2} y={base + 18} textAnchor="middle" className="lab">{c.labels[i]}</text>
            </g>
          );
        })}
      </svg>
    </figure>
  );
}

export function DataDrill({ level, seed, onDone }: DrillProps) {
  const questions = useMemo(() => makeDataRound(level, seed), [level, seed]);
  return (
    <ChoiceDrill
      questions={questions} seed={seed} onDone={onDone} className="data-drill"
      info={(q) => ({ key: q.key, ask: q.ask, answer: q.options[q.answer] })}
      choicesClass={(q) => (q.options.some((o) => o.length > 14) ? 'one-col' : '')}
      render={(q) => (
        <div className="data-q">
          {q.data.kind === 'table' ? <Table t={q.data} /> : <Chart c={q.data} />}
          <p className="lang-ask small-ask">{q.ask}</p>
        </div>
      )}
      renderChoice={(q, i) => <span className="choice-text">{q.options[i]}</span>}
    />
  );
}
